import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import ApiService from '../../services/api';
import {
  IceCream,
  BarChart3,
  Users,
  Truck,
  Settings,
  LogOut,
  Home,
  Package,
  MapPin,
  Calendar,
  Coffee,
  Menu,
  ChevronLeft,
  ChevronRight,
  Mail,
  ClipboardList,
  FileText,
  Tag,
  AlertTriangle,
  ChevronDown,
  Beaker,
  Shield,
  Cloud,
  Archive,
  TrendingUp,
  Trash2,
  CreditCard,
  ShoppingCart,
  Banknote,
  Clock,
  IceCreamCone,
  Monitor as MonitorIcon,
  Table,
  BookUser,
  LifeBuoy,
} from 'lucide-react';

const Header = styled.header`
  background: var(--primary-700);
  color: white;
  padding: 1rem 2rem;
  height: var(--header-height, 70px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: var(--shadow-sm);
  position: sticky;
  top: 0;
  z-index: 1000;

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    height: var(--header-height, 60px);
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.5rem;
  font-weight: bold;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 1.15rem;
    gap: 0.25rem;
    svg {
      width: 24px;
      height: 24px;
    }
  }

  @media (max-width: 480px) {
    span.logo-text {
      display: none;
    }
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ImpersonateWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.15);
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  font-size: 0.85rem;
  color: white;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  @media (max-width: 768px) {
    span {
      display: none;
    }
  }
`;

const ImpersonateSelect = styled.select`
  background: transparent;
  border: none;
  color: white;
  font-weight: 500;
  outline: none;
  cursor: pointer;
  padding-right: 0.5rem;
  font-size: 0.85rem;
  max-width: 100%;
  
  option {
    color: #333;
    background: white;
  }

  @media (max-width: 768px) {
    max-width: 110px;
    text-overflow: ellipsis;
  }
`;

const StopImpersonatingButton = styled.button`
  background: var(--accent-600);
  color: white;
  border: none;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  box-shadow: var(--shadow-sm);
  transition: background 0.2s ease;

  &:hover {
    background: var(--accent-700);
  }

  @media (max-width: 768px) {
    span {
      display: none;
    }
  }
`;

const UserName = styled.span`
  font-weight: 500;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  @media (max-width: 768px) {
    display: none;
  }
`;

const LogoutButton = styled.button`
  background: rgba(255,255,255,0.2);
  border: none;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background 0.2s;

  &:hover {
    background: rgba(255,255,255,0.3);
  }

  @media (max-width: 768px) {
    padding: 0.5rem;
    span {
      display: none;
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: rgba(255,255,255,0.2);
  border: none;
  color: white;
  width: 44px;
  height: 44px;
  border-radius: 6px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255,255,255,0.3);
  }
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const Container = styled.div`
  display: flex;
  min-height: calc(100vh - var(--header-height, 70px));
  width: 100%;
  overflow: hidden;

  @media (max-width: 768px) {
    min-height: calc(100vh - var(--header-height, 60px));
  }
`;

const MobileOverlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: var(--header-height, 60px);
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.5);
    z-index: 998;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s ease;
    
    &.open {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

const Sidebar = styled.nav`
  width: 250px;
  background: var(--gray-50);
  border-right: 1px solid var(--gray-200);
  padding: 1rem 0;
  transition: all 0.3s ease;
  position: relative;
  
  &.collapsed {
    width: 60px;
  }
  
  @media (max-width: 768px) {
    width: 250px;
    position: fixed;
    top: var(--header-height, 60px);
    left: 0;
    height: calc(100vh - var(--header-height, 60px));
    background: white;
    z-index: 999;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    
    &.open {
      transform: translateX(0);
    }
    
    &.collapsed {
      width: 250px;
    }
  }
`;

const ToggleSidebarButton = styled.button`
  position: absolute;
  top: 10px;
  right: -15px;
  width: 30px;
  height: 30px;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  z-index: 1000;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  flex-shrink: 0;
`;

const SubNav = styled.div`
  padding-left: 1rem;
  background: var(--gray-100);
  border-left: 3px solid var(--primary-500);
  max-height: 0;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &.open {
    max-height: 500px;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
`;

const SubNavItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s;
  gap: 0.75rem;
  font-size: 0.9rem;
  
  &:hover {
    background: var(--primary-100);
    color: var(--primary-800);
  }

  &.active {
    background: var(--primary-700);
    color: white;
  }
`;

const ExpandIcon = styled.div`
  margin-left: auto;
  transition: transform 0.3s ease;
  
  &.expanded {
    transform: rotate(180deg);
  }
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s;
  gap: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;

  &:hover {
    background: var(--primary-100);
    color: var(--primary-800);
  }

  &.active {
    background: var(--primary-700);
    color: white;
    border-right: 3px solid var(--primary-800);
  }
  
  &.has-submenu {
    justify-content: space-between;
  }
  
  .nav-text {
    transition: opacity 0.3s ease, width 0.3s ease;
    overflow: hidden;
  }
  
  .sidebar.collapsed & {
    padding: 1rem;
    justify-content: center;
    
    .nav-text {
      opacity: 0;
      width: 0;
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    .nav-text {
      opacity: 1;
      width: auto;
      display: inline;
    }
  }
`;

const MainContent = styled.main`
  flex: 1;
  min-width: 0;
  padding: 2rem;
  background: var(--bg-primary);
  overflow-y: auto;
  overflow-x: hidden;
  transition: all 0.3s ease;

  @media (max-width: 1024px) {
    padding: 1rem;
  }

  @media (max-width: 768px) {
    padding: 0.75rem;
    width: 100%;
  }
`;

const Badge = styled.span`
  background: var(--primary-800);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
`;

interface LayoutProps {
  children: React.ReactNode;
  currentPage: string;
  onNavigate: (page: string) => void;
}

const Layout: React.FC<LayoutProps> = ({ children, currentPage, onNavigate }) => {
  const { user, logout, isImpersonating, originalUser, impersonateUser, stopImpersonating } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [laboratorioExpanded, setLaboratorioExpanded] = useState(false);
  const [cassaExpanded, setCassaExpanded] = useState(false);
  const [analisiExpanded, setAnalisiExpanded] = useState(false);
  const [anagraficheExpanded, setAnagraficheExpanded] = useState(false);
  const [usersList, setUsersList] = useState<any[]>([]);
  const [selectedUserVal, setSelectedUserVal] = useState<string>('');

  const isAdminOrImpersonatingAdmin = user?.ruolo === 'admin' || (originalUser && originalUser.ruolo === 'admin');

  useEffect(() => {
    const fetchUsers = async () => {
      if (isAdminOrImpersonatingAdmin) {
        try {
          const apiUsers = await ApiService.getUsers();
          const currentAdminId = originalUser?.id || user?.id;
          const filtered = apiUsers.filter(u => u.attivo && u.id !== currentAdminId);
          setUsersList(filtered);
        } catch (err) {
          console.error('Errore recupero utenti per impersonificazione:', err);
        }
      }
    };
    fetchUsers();
  }, [isAdminOrImpersonatingAdmin, user?.id, originalUser?.id]);

  useEffect(() => {
    if (isImpersonating && user) {
      setSelectedUserVal(user.id.toString());
    } else {
      setSelectedUserVal('');
    }
  }, [isImpersonating, user]);

  const handleImpersonateChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (!val) {
      if (isImpersonating) {
        stopImpersonating();
      }
      return;
    }
    
    try {
      await impersonateUser(parseInt(val));
    } catch (err) {
      alert('Impossibile impersonare l\'utente selezionato');
      console.error(err);
    }
  };

  // Il nome dell'attivita' viene dal tenant. Il ripiego e' il nome del
  // prodotto, non quello di una gelateria: il sistema e' multi-tenant e
  // vederci scritto il nome di un'altra attivita' non avrebbe senso.
  const displayName = user?.tenant_name || 'Sistema Gelato';

  // Espandi automaticamente i menu se si è in una pagina correlata
  useEffect(() => {
    if (isSettingsActive()) {
      setSettingsExpanded(true);
    }
    if (isLaboratorioActive()) {
      setLaboratorioExpanded(true);
    }
    if (isCassaActive()) {
      setCassaExpanded(true);
    }
    if (isAnalisiActive()) {
      setAnalisiExpanded(true);
    }
    if (isAnagraficheActive()) {
      setAnagraficheExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deve rieseguire solo al cambio pagina; le funzioni is*Active sono ricreate ad ogni render
  }, [currentPage]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Errore logout:', error);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const getNavigationItems = () => {
    const baseItems = [
      { id: 'dashboard', label: 'Dashboard', icon: Home }
    ];

    baseItems.push({ id: 'turni', label: 'Turni', icon: Clock });

    // Il banco: chi ci sta dietro spegne i gusti finiti. La voce e' per tutti
    // tranne chi non lavora in negozio.
    baseItems.push({ id: 'banco', label: 'Banco', icon: IceCreamCone });

    // La configurazione degli schermi non sta nella pagina Banco: quella vive
    // su un tablet sul bancone, davanti ai clienti.
    if (['manager', 'admin'].includes(user?.ruolo || '')) {
      baseItems.push({ id: 'impostazioni-monitor', label: 'Monitor', icon: MonitorIcon });
    }

    // Ogni dipendente accede alla propria busta paga: la voce e' per tutti, la
    // pagina mostra l'archivio completo solo a chi gestisce il personale.
    baseItems.push({ id: 'personale', label: 'Personale', icon: FileText });

    // L'assistenza e' per tutti e non solo per chi amministra: chi ha un
    // problema davanti deve poterla trovare senza chiedere a qualcun altro.
    baseItems.push({ id: 'assistenza', label: 'Assistenza', icon: LifeBuoy });

    return baseItems;
  };

  const getLaboratorioItems = () => {
    const items = [];
    if (user?.ruolo !== 'banconista') {
      items.push(
        { id: 'produzione', label: 'Produzione', icon: IceCream },
        { id: 'smaltimento', label: 'Smaltimento', icon: Trash2 },
        { id: 'storico-produzione', label: 'Storico Produzione', icon: Calendar },
        { id: 'calendario-produzione', label: 'Calendario', icon: Calendar }
      );
    }
    // Tutti, incluso banconista, vedono Giacenza
    items.push({ id: 'giacenza', label: 'Giacenza', icon: Archive });

    if (user?.ruolo === 'admin') {
      items.push(
        { id: 'gusti', label: 'Gusti', icon: Package },
        { id: 'ingredienti', label: 'Ingredienti', icon: Coffee }
      );
    }

    if (user?.ruolo === 'admin') {
      items.push({ id: 'stampe', label: 'Stampe', icon: Beaker });
    }

    return items;
  };

  const getSettingsItems = () => {
    if (user?.ruolo !== 'admin') return [];

    return [
      { id: 'impostazioni-menu', label: 'Menu Prodotti', icon: Menu },
      { id: 'impostazioni-categorie-gusti', label: 'Categorie Gusti', icon: Tag },
      { id: 'impostazioni-allergeni', label: 'Allergeni', icon: AlertTriangle },
      { id: 'impostazioni-integrazioni', label: 'Integrazioni (POS)', icon: CreditCard },
      { id: 'impostazioni-email', label: 'Email', icon: Mail },
      { id: 'impostazioni-meteo', label: 'Meteo', icon: Cloud },
      { id: 'settings', label: 'Database', icon: Settings },
      { id: 'log-attivita', label: 'Log Attività', icon: ClipboardList },
      { id: 'storico-email', label: 'Storico Email', icon: Archive }
    ];
  };

  const isLaboratorioActive = () => {
    const laboratorioPages = ['produzione', 'giacenza', 'smaltimento', 'storico-produzione', 'calendario-produzione', 'gusti', 'ingredienti', 'previsione-annuale', 'analisi-previsioni', 'stampe'];
    return laboratorioPages.includes(currentPage);
  };

  const isSettingsActive = () => {
    const settingsPages = ['impostazioni-menu', 'impostazioni-integrazioni', 'impostazioni-categorie-gusti', 'impostazioni-allergeni', 'impostazioni-email', 'impostazioni-meteo', 'settings', 'log-attivita', 'storico-email'];
    return settingsPages.includes(currentPage);
  };

  const toggleSettings = () => {
    setSettingsExpanded(!settingsExpanded);
  };

  const toggleLaboratorio = () => {
    setLaboratorioExpanded(!laboratorioExpanded);
  };

  const getCassaItems = () => {
    if (user?.ruolo !== 'admin' && user?.ruolo !== 'manager' && user?.ruolo !== 'banconista') return [];
    
    const items: { id: string; label: string; icon: any }[] = [];
    
    if (user?.ruolo === 'banconista' || user?.ruolo === 'manager' || user?.ruolo === 'admin') {
      items.push({ id: 'incasso', label: 'Incasso del Giorno', icon: Banknote });
    }

    if (user?.ruolo === 'admin' || user?.ruolo === 'manager') {
      items.push(
        { id: 'cassa-ticket', label: 'Ticket / Consegne', icon: ClipboardList },
        { id: 'versamento-cassa', label: 'Versamento', icon: Banknote }
      );
    }
    if (user?.ruolo === 'admin') {
      items.push(
        { id: 'kiara-dati', label: 'Dati Cassa', icon: ShoppingCart },
        { id: 'kiara-imports', label: 'Import Kiara', icon: FileText }
      );
    }
    return items;
  };

  // Tutte le viste di analisi sotto un solo cappello, riservate all'admin.
  const getAnalisiItems = () => {
    if (user?.ruolo !== 'admin') return [];
    return [
      { id: 'analytics', label: 'Analytics', icon: BarChart3 },
      { id: 'tabelle-analisi', label: 'Tabelle Analisi', icon: Table },
      { id: 'previsione-annuale', label: 'Previsione Annuale', icon: TrendingUp },
      { id: 'analisi-previsioni', label: 'Analisi Previsioni', icon: TrendingUp },
      { id: 'controllo-gestione', label: 'Controllo di gestione', icon: TrendingUp }
    ];
  };

  const getAnagraficheItems = () => {
    if (user?.ruolo !== 'admin') return [];
    return [
      { id: 'users', label: 'Utenti', icon: Users },
      { id: 'punti-vendita', label: 'Punti Vendita', icon: MapPin },
      { id: 'fornitori', label: 'Fornitori', icon: Truck }
    ];
  };

  const isAnalisiActive = () => {
    return ['analytics', 'tabelle-analisi', 'previsione-annuale', 'analisi-previsioni', 'controllo-gestione'].includes(currentPage);
  };

  const isAnagraficheActive = () => {
    return ['users', 'punti-vendita', 'fornitori'].includes(currentPage);
  };

  const toggleAnalisi = () => {
    setAnalisiExpanded(!analisiExpanded);
  };

  const toggleAnagrafiche = () => {
    setAnagraficheExpanded(!anagraficheExpanded);
  };

  const isCassaActive = () => {
    return ['cassa-ticket', 'versamento-cassa', 'kiara-dati', 'kiara-imports', 'incasso'].includes(currentPage);
  };

  const toggleCassa = () => {
    setCassaExpanded(!cassaExpanded);
  };

  return (
    <>
      <Header>
        <Logo>
          <IceCream size={32} className="logo-icon" />
          <span className="logo-text">{displayName}</span>
        </Logo>
        <UserInfo>
          {isAdminOrImpersonatingAdmin && (
            <ImpersonateWrapper>
              <Users size={16} />
              <span>Impersona:</span>
              <ImpersonateSelect value={selectedUserVal} onChange={handleImpersonateChange}>
                <option value="">-- Seleziona Utente --</option>
                {usersList.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nome} ({u.ruolo})
                  </option>
                ))}
              </ImpersonateSelect>
            </ImpersonateWrapper>
          )}

          {isImpersonating && (
            <StopImpersonatingButton onClick={stopImpersonating} title="Termina impersonificazione">
              <Shield size={16} />
              <span>Ripristina Admin</span>
            </StopImpersonatingButton>
          )}

          <MobileMenuButton onClick={toggleSidebar}>
            <Menu size={24} />
          </MobileMenuButton>
          <UserDetails>
            <UserName>
              {user?.nome}
              {isImpersonating && <span style={{ fontSize: '0.85rem', fontWeight: 'bold', marginLeft: '0.25rem', color: '#FFF' }}>(Sudo)</span>}
            </UserName>
            <Badge>{user?.ruolo}</Badge>
          </UserDetails>
          <LogoutButton onClick={handleLogout}>
            <LogOut size={20} />
            <span>Esci</span>
          </LogoutButton>
        </UserInfo>
      </Header>

      <Container>
        <MobileOverlay 
          className={sidebarOpen ? 'open' : ''} 
          onClick={() => setSidebarOpen(false)} 
        />
        <Sidebar className={`sidebar ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <ToggleSidebarButton onClick={toggleSidebarCollapse}>
            {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </ToggleSidebarButton>

          {/* Menu principale */}
          {getNavigationItems().map(item => {
            const Icon = item.icon;
            return (
              <NavItem
                key={item.id}
                className={currentPage === item.id ? 'active' : ''}
                onClick={() => {
                  onNavigate(item.id);
                  if (window.innerWidth <= 768) {
                    setSidebarOpen(false);
                  }
                }}
              >
                <NavIconContainer>
                  <Icon size={20} />
                </NavIconContainer>
                <span className="nav-text">{item.label}</span>
              </NavItem>
            );
          })}

          {/* Menu Laboratorio */}
          <NavItem
            className={`has-submenu ${isLaboratorioActive() ? 'active' : ''}`}
            onClick={toggleLaboratorio}
          >
            <NavIconContainer>
              <Beaker size={20} />
            </NavIconContainer>
            <span className="nav-text">Laboratorio</span>
            {!sidebarCollapsed && (
              <ExpandIcon className={laboratorioExpanded ? 'expanded' : ''}>
                <ChevronDown size={16} />
              </ExpandIcon>
            )}
          </NavItem>

          <SubNav className={laboratorioExpanded ? 'open' : ''}>
            {getLaboratorioItems().map(item => {
              const Icon = item.icon;
              return (
                <SubNavItem
                  key={item.id}
                  className={currentPage === item.id ? 'active' : ''}
                  onClick={() => {
                    onNavigate(item.id);
                    if (window.innerWidth <= 768) {
                      setSidebarOpen(false);
                    }
                  }}
                >
                  <NavIconContainer>
                    <Icon size={16} />
                  </NavIconContainer>
                  <span>{item.label}</span>
                </SubNavItem>
              );
            })}
          </SubNav>

          {/* Menu Cassa per admin, manager e banconista */}
          {(user?.ruolo === 'admin' || user?.ruolo === 'manager' || user?.ruolo === 'banconista') && (
            <>
              <NavItem
                className={`has-submenu ${isCassaActive() ? 'active' : ''}`}
                onClick={toggleCassa}
              >
                <NavIconContainer>
                  <CreditCard size={20} />
                </NavIconContainer>
                <span className="nav-text">Cassa</span>
                {!sidebarCollapsed && (
                  <ExpandIcon className={cassaExpanded ? 'expanded' : ''}>
                    <ChevronDown size={16} />
                  </ExpandIcon>
                )}
              </NavItem>

              <SubNav className={cassaExpanded ? 'open' : ''}>
                {getCassaItems().map(item => {
                  const Icon = item.icon;
                  return (
                    <SubNavItem
                      key={item.id}
                      className={currentPage === item.id ? 'active' : ''}
                      onClick={() => {
                        onNavigate(item.id);
                        if (window.innerWidth <= 768) setSidebarOpen(false);
                      }}
                    >
                      <NavIconContainer>
                        <Icon size={16} />
                      </NavIconContainer>
                      <span>{item.label}</span>
                    </SubNavItem>
                  );
                })}
              </SubNav>
            </>
          )}

          {/* Menu Analisi per admin */}
          {user?.ruolo === 'admin' && (
            <>
              <NavItem
                className={`has-submenu ${isAnalisiActive() ? 'active' : ''}`}
                onClick={toggleAnalisi}
              >
                <NavIconContainer>
                  <BarChart3 size={20} />
                </NavIconContainer>
                <span className="nav-text">Analisi</span>
                {!sidebarCollapsed && (
                  <ExpandIcon className={analisiExpanded ? 'expanded' : ''}>
                    <ChevronDown size={16} />
                  </ExpandIcon>
                )}
              </NavItem>

              <SubNav className={analisiExpanded ? 'open' : ''}>
                {getAnalisiItems().map(item => {
                  const Icon = item.icon;
                  return (
                    <SubNavItem
                      key={item.id}
                      className={currentPage === item.id ? 'active' : ''}
                      onClick={() => {
                        onNavigate(item.id);
                        if (window.innerWidth <= 768) setSidebarOpen(false);
                      }}
                    >
                      <NavIconContainer>
                        <Icon size={16} />
                      </NavIconContainer>
                      <span>{item.label}</span>
                    </SubNavItem>
                  );
                })}
              </SubNav>
            </>
          )}

          {/* Menu Anagrafiche per admin */}
          {user?.ruolo === 'admin' && (
            <>
              <NavItem
                className={`has-submenu ${isAnagraficheActive() ? 'active' : ''}`}
                onClick={toggleAnagrafiche}
              >
                <NavIconContainer>
                  <BookUser size={20} />
                </NavIconContainer>
                <span className="nav-text">Anagrafiche</span>
                {!sidebarCollapsed && (
                  <ExpandIcon className={anagraficheExpanded ? 'expanded' : ''}>
                    <ChevronDown size={16} />
                  </ExpandIcon>
                )}
              </NavItem>

              <SubNav className={anagraficheExpanded ? 'open' : ''}>
                {getAnagraficheItems().map(item => {
                  const Icon = item.icon;
                  return (
                    <SubNavItem
                      key={item.id}
                      className={currentPage === item.id ? 'active' : ''}
                      onClick={() => {
                        onNavigate(item.id);
                        if (window.innerWidth <= 768) setSidebarOpen(false);
                      }}
                    >
                      <NavIconContainer>
                        <Icon size={16} />
                      </NavIconContainer>
                      <span>{item.label}</span>
                    </SubNavItem>
                  );
                })}
              </SubNav>
            </>
          )}

          {/* Menu Impostazioni per admin */}
          {user?.ruolo === 'admin' && (
            <>
              <NavItem
                className={`has-submenu ${isSettingsActive() ? 'active' : ''}`}
                onClick={toggleSettings}
              >
                <NavIconContainer>
                  <Settings size={20} />
                </NavIconContainer>
                <span className="nav-text">Impostazioni</span>
                {!sidebarCollapsed && (
                  <ExpandIcon className={settingsExpanded ? 'expanded' : ''}>
                    <ChevronDown size={16} />
                  </ExpandIcon>
                )}
              </NavItem>

              <SubNav className={settingsExpanded ? 'open' : ''}>
                {getSettingsItems().map(item => {
                  const Icon = item.icon;
                  return (
                    <SubNavItem
                      key={item.id}
                      className={currentPage === item.id ? 'active' : ''}
                      onClick={() => {
                        onNavigate(item.id);
                        if (window.innerWidth <= 768) {
                          setSidebarOpen(false);
                        }
                      }}
                    >
                      <NavIconContainer>
                        <Icon size={16} />
                      </NavIconContainer>
                      <span>{item.label}</span>
                    </SubNavItem>
                  );
                })}
              </SubNav>
            </>
          )}
        </Sidebar>

        <MainContent>
          {children}
        </MainContent>
      </Container>
    </>
  );
};

export default Layout;
